/**
 * Reportes → Ventas de productos (`/reportes/venta-productos`).
 *
 * Puerto de `Reportes::venta_productos` + `Reportes::resumen_ventas_producto` +
 * `Reportes_model::get_serie_grouped_like{,_admin}` +
 * `views/menu/reportes/venta_productos.php`.
 *
 * La pantalla del panel anterior abría un modal con el **mes**, el **producto**,
 * el **tipo de comprobante** (facturas y boletas, sólo facturas, sólo boletas,
 * notas de venta, notas de crédito o anulados) y **las series** (select2
 * múltiple), y pintaba las líneas vendidas (documento, cliente, serie, número,
 * fecha, base, IGV, total, producto, precio, cantidad, total y método de pago).
 * El pie sumaba base, IGV, total, cantidad y el «total venta» de la línea a
 * precio de lista.
 *
 * Reglas conservadas:
 * - Sin mes elegido se consulta el **mes en curso**.
 * - Las líneas son las de `venta_producto` de las facturas, las boletas, las
 *   ventas de administración y las notas (de venta y de crédito); el filtro por
 *   series sólo se aplica a las facturas y a las boletas, como antes.
 * - Los importes viajan sin redondear y el pie suma lo mismo que el anterior.
 *
 * Diferencias anotadas:
 * - La opción **TODOS** del desplegable de productos ya no vacía el reporte: la
 *   consulta anterior filtraba `producto_id = 0` con el valor de esa opción, así
 *   que sólo se veían datos al elegir un producto.
 * - Las boletas **«Anulado por NC»** (las que reemplazó una nota de crédito) ya
 *   no se cuentan como venta válida; antes se listaban con el resto.
 * - El tipo **Anulado** ya devuelve las notas anuladas (en las notas el estado
 *   vive en `estado`, no en `estado_api`, que siempre es nulo; la consulta
 *   anterior pedía las dos cosas a la vez y nunca devolvía nada).
 * - Se añade el **resumen por producto** (líneas, cantidad e importes por
 *   artículo), el atajo de **meses con ventas** y la exportación en **CSV**
 *   servida por la API.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosVentaProductos,
  useVentaProductos,
  type FiltrosVentaProductos,
  type LineaVentaProducto,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

// El filtro por serie sólo tiene sentido en los comprobantes que lo aplican
// (las notas siempre se listan completas, como en el panel anterior).
const COMPROBANTES_CON_SERIES = ['FACTURABOLETA', 'FACTURA', 'BOLETA', 'ANULADO'];

/** Mes en curso como `AAAA-MM` (el valor por defecto del panel anterior). */
function mesActual(): string {
  const hoy = new Date();

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string | null | undefined): string {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo ?? '';

  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

/** Tono del badge según el comprobante de la línea. */
function tonoDelOrigen(origen: string): 'brand' | 'neutral' | 'warning' | 'danger' {
  if (origen === 'FACTURA') return 'brand';
  if (origen === 'BOLETA') return 'neutral';
  if (origen === 'NOTA DE CREDITO') return 'danger';

  return 'warning';
}

/** Nombre corto del comprobante, como se lee en la tabla. */
function nombreDelOrigen(origen: string): string {
  if (origen === 'ADMINISTRACION') return 'ADMIN.';
  if (origen === 'NOTA DE VENTA') return 'N. VENTA';
  if (origen === 'NOTA DE CREDITO') return 'N. CRÉDITO';

  return origen;
}

export function ReporteVentaProductosPage() {
  useDocumentTitle('Reportes · Ventas de productos');

  const [filtros, setFiltros] = useState<FiltrosVentaProductos>({
    periodo: mesActual(),
    producto: '0',
    comprobante: 'FACTURABOLETA',
    series: [],
  });
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosVentaProductos();
  const consulta = useVentaProductos(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const meses = catalogos.data?.meses ?? [];
  const ultimoMesConDatos = meses[0]?.periodo ?? null;
  const sinLineas = Boolean(
    resumen && resumen.lineas === 0 && ultimoMesConDatos && ultimoMesConDatos !== filtros.periodo,
  );

  /** Productos agrupados por tipo, como el desplegable del panel anterior. */
  const productosPorTipo = useMemo(() => {
    const grupos = new Map<
      string,
      { id: number; nombre: string; precio_venta: number; activo: boolean }[]
    >();

    for (const producto of catalogos.data?.productos ?? []) {
      const tipo = producto.tipo || 'SIN TIPO';
      const lista = grupos.get(tipo) ?? [];

      lista.push(producto);
      grupos.set(tipo, lista);
    }

    return [...grupos.entries()];
  }, [catalogos.data]);

  /** Series del comprobante elegido (facturas, boletas o ninguna). */
  const series = useMemo(() => {
    const listas = catalogos.data?.series;

    if (filtros.comprobante === 'FACTURA') return listas?.factura ?? [];
    if (filtros.comprobante === 'BOLETA') return listas?.boleta ?? [];

    return [...(listas?.factura ?? []), ...(listas?.boleta ?? [])];
  }, [catalogos.data, filtros.comprobante]);

  const lineas = useMemo(() => datos?.lineas ?? [], [datos]);
  // El listado puede traer miles de líneas del mes: se pagina en el navegador y el pie
  // sigue sumando el listado completo.
  const paginacion = usePaginacion(lineas, { clave: filtros });

  const cambiar = <C extends keyof FiltrosVentaProductos>(
    campo: C,
    valor: FiltrosVentaProductos[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  /** Cambiar el comprobante limpia las series, como hacía el panel anterior. */
  const cambiarComprobante = (comprobante: string) =>
    setFiltros((previos) => ({ ...previos, comprobante, series: [] }));

  const alternarSerie = (serie: string, marcada: boolean) =>
    setFiltros((previos) => ({
      ...previos,
      series: marcada ? [...previos.series, serie] : previos.series.filter((s) => s !== serie),
    }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarVentaProductos(filtros), {
        nombre: `venta-productos-${filtros.comprobante.toLowerCase()}-${filtros.periodo}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  /** Abre el PDF del reporte (mismas líneas, para imprimir o archivar). */
  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfVentaProductos(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const etiquetaComprobante =
    catalogos.data?.comprobantes.find((tipo) => tipo.valor === filtros.comprobante)?.etiqueta ??
    filtros.comprobante;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Ventas de productos</h1>
          <p className="mt-1 text-sm text-ink-500">
            Líneas vendidas de un mes, con su comprobante, cliente, precio y método de pago.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Mes</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="month"
                  value={filtros.periodo}
                  onChange={(evento) => cambiar('periodo', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Producto</span>
              <Select
                value={filtros.producto}
                onChange={({ target }) => cambiar('producto', target.value)}
                className={CONTROL}
                aria-label="Producto"
              >
                <option value="0">TODOS</option>
                {productosPorTipo.map(([tipo, productos]) => (
                  <optgroup key={tipo} label={tipo}>
                    {productos.map((producto) => (
                      <option key={producto.id} value={String(producto.id)}>
                        {producto.nombre}
                        {producto.activo ? '' : ' (inactivo)'}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Comprobante</span>
              <Select
                value={filtros.comprobante}
                onChange={({ target }) => cambiarComprobante(target.value)}
                className={CONTROL}
                aria-label="Tipo de comprobante"
              >
                {(catalogos.data?.comprobantes ?? []).map((tipo) => (
                  <option key={tipo.valor} value={tipo.valor}>
                    {tipo.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">
                Series{' '}
                <span className="font-normal text-ink-400">
                  {filtros.series.length === 0
                    ? '(todas)'
                    : `(${filtros.series.length} elegida(s))`}
                </span>
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                {!COMPROBANTES_CON_SERIES.includes(filtros.comprobante) ? (
                  <span className="text-xs text-ink-400">
                    Las notas no usan el filtro de series: se listan completas.
                  </span>
                ) : null}

                {COMPROBANTES_CON_SERIES.includes(filtros.comprobante) && series.length === 0 ? (
                  <span className="text-xs text-ink-400">Sin series registradas.</span>
                ) : null}

                {COMPROBANTES_CON_SERIES.includes(filtros.comprobante)
                  ? series.map((serie) => {
                      const marcada = filtros.series.includes(serie);

                      return (
                        <button
                          key={serie}
                          type="button"
                          aria-pressed={marcada}
                          onClick={() => alternarSerie(serie, !marcada)}
                          className={cn(
                            'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                            marcada
                              ? 'border-brand-300 bg-brand-50 text-brand-700'
                              : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                          )}
                        >
                          {serie}
                        </button>
                      );
                    })
                  : null}

                {filtros.series.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => cambiar('series', [])}
                    className="ml-1 text-xs font-semibold text-ink-500 hover:text-brand-700 hover:underline"
                  >
                    Limpiar
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con ventas
              </span>
              {meses.slice(0, 12).map((mes) => (
                <button
                  key={mes.periodo}
                  type="button"
                  aria-pressed={mes.periodo === filtros.periodo}
                  onClick={() => cambiar('periodo', mes.periodo)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                    mes.periodo === filtros.periodo
                      ? 'border-brand-300 bg-brand-50 text-brand-700'
                      : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                  )}
                  title={`${formatNumber(mes.lineas)} línea(s)`}
                >
                  {etiquetaMes(mes.periodo)}
                </button>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      {sinLineas ? (
        <Alert variant="info" title="El mes elegido no tiene ventas de productos">
          <p>
            Esta copia tiene líneas vendidas entre {etiquetaMes(catalogos.data?.rango.desde)} y{' '}
            {etiquetaMes(catalogos.data?.rango.hasta)}.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => cambiar('periodo', ultimoMesConDatos ?? '')}
          >
            Ver {etiquetaMes(ultimoMesConDatos)}
          </Button>
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {[
          {
            etiqueta: 'Documentos',
            valor: formatNumber(resumen?.documentos ?? 0),
            detalle: `${formatNumber(resumen?.lineas ?? 0)} línea(s)`,
          },
          {
            etiqueta: 'Productos',
            valor: formatNumber(resumen?.productos ?? 0),
            detalle: `${formatNumber(resumen?.clientes ?? 0)} cliente(s)`,
          },
          {
            etiqueta: 'Cantidad vendida',
            valor: formatNumber(resumen?.cantidad ?? 0),
            detalle: 'según la unidad del producto',
          },
          {
            etiqueta: 'Total cobrado',
            valor: formatCurrency(resumen?.total ?? 0),
            detalle: `base ${formatNumber(resumen?.base ?? 0)} · IGV ${formatNumber(
              resumen?.igv ?? 0,
            )}`,
          },
          {
            etiqueta: 'Total a precio de lista',
            valor: formatCurrency(resumen?.total_venta ?? 0),
            detalle: 'la columna «total venta» anterior',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {(datos?.por_producto.length ?? 0) > 0 ? (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-ink-800">Ventas por producto</h2>
              <span className="text-xs text-ink-500">
                {etiquetaMes(datos?.filtros.descripcion ?? filtros.periodo)} ·{' '}
                {etiquetaComprobante}
              </span>
            </div>

            <div className="overflow-x-auto rounded-card border border-ink-100">
              <table className="w-full min-w-[40rem] border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Producto</th>
                    <th className="w-[7rem] px-2 py-2">Tipo</th>
                    <th className="w-[4.5rem] px-2 py-2 text-right">Líneas</th>
                    <th className="w-[6rem] px-2 py-2 text-right">Cantidad</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Total</th>
                    <th className="w-[7.5rem] px-2 py-2 text-right">Total venta</th>
                  </tr>
                </thead>
                <tbody>
                  {datos?.por_producto.map((fila) => (
                    <tr key={fila.producto_id} className="border-t border-ink-100">
                      <td className="px-2 py-2 font-medium text-ink-800">{fila.producto}</td>
                      <td className="px-2 py-2 text-ink-500">{fila.tipo || '—'}</td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {formatNumber(fila.lineas)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.cantidad)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.total)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatCurrency(fila.total_venta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                  <tr className="text-[13px] font-semibold text-ink-800">
                    <td className="px-2 py-2" colSpan={2}>
                      SUMA · {formatNumber(resumen?.productos ?? 0)} producto(s)
                    </td>
                    <td className="px-2 py-2 text-right">{formatNumber(resumen?.lineas ?? 0)}</td>
                    <td className="px-2 py-2 text-right">{formatNumber(resumen?.cantidad ?? 0)}</td>
                    <td className="px-2 py-2 text-right">{formatCurrency(resumen?.total ?? 0)}</td>
                    <td className="px-2 py-2 text-right">
                      {formatCurrency(resumen?.total_venta ?? 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Líneas vendidas ({formatNumber(resumen?.lineas ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {etiquetaComprobante} ·{' '}
              {filtros.series.length > 0 ? filtros.series.join(', ') : 'todas las series'}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
              <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full max-lg:min-w-[46rem] table-fixed border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="w-[4.75rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[6.25rem] px-1.5 py-2">Comprobante</th>
                    <th className="w-[7.5rem] px-1.5 py-2">Cliente</th>
                    <th className="w-[7rem] px-1.5 py-2">Producto</th>
                    <th className="w-[3.75rem] px-1.5 py-2 text-right">Cant.</th>
                    <th className="w-[7rem] px-1.5 py-2 text-right">Total</th>
                    <th className="w-[6rem] px-1.5 py-2">Pago</th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={7} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay ventas de productos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila: LineaVentaProducto, indice: number) => (
                    <tr
                      key={`${fila.origen}-${fila.comprobante_id}-${indice}`}
                      className="border-t border-ink-100 hover:bg-brand-50/30"
                    >
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900">
                          {formatDateValue(fila.fecha)}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-medium text-ink-800">
                          {[fila.serie, fila.numero].filter(Boolean).join('-') || '—'}
                        </p>
                        <Badge
                          variant={tonoDelOrigen(fila.origen)}
                          className="mt-0.5 px-1.5 py-0 text-[10px]"
                        >
                          {nombreDelOrigen(fila.origen)}
                        </Badge>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.cliente ?? ''}>
                          {fila.cliente ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">{fila.documento ?? ''}</p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.producto ?? ''}>
                          {fila.producto ?? '—'}
                        </p>
                        <p
                          className="truncate text-[11px] text-ink-500"
                          title={`Precio aplicado ${formatNumber(fila.precio_unidad ?? 0)} · precio de lista ${formatNumber(fila.precio ?? 0)}`}
                        >
                          {formatCurrency(fila.precio_unidad ?? 0)} / u.
                        </p>
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        <p className="truncate">{formatNumber(fila.cantidad ?? 0)}</p>
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        <p className="truncate font-semibold text-ink-900">
                          {formatCurrency(fila.total ?? 0)}
                        </p>
                        <p
                          className="truncate text-[11px] text-ink-500"
                          title={`Base ${formatNumber(fila.base ?? 0)} · IGV ${formatNumber(
                            fila.igv ?? 0,
                          )} · a precio de lista ${formatNumber(fila.total_venta ?? 0)}`}
                        >
                          B {formatNumber(fila.base ?? 0)} · I {formatNumber(fila.igv ?? 0)}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-600" title={fila.metodo_pago ?? ''}>
                          {fila.metodo_pago ?? '—'}
                        </p>
                        {fila.estado_documento ? (
                          <p className="truncate text-[11px] text-ink-500">
                            {fila.estado_documento}
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {lineas.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={4}>
                        SUMA · {formatNumber(resumen?.documentos ?? 0)} documento(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatNumber(resumen?.cantidad ?? 0)}
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        <p className="truncate">{formatCurrency(resumen?.total ?? 0)}</p>
                        <p
                          className="truncate text-[11px] font-normal text-ink-500"
                          title={`Base ${formatNumber(resumen?.base ?? 0)} · IGV ${formatNumber(
                            resumen?.igv ?? 0,
                          )} · a precio de lista ${formatNumber(resumen?.total_venta ?? 0)}`}
                        >
                          B {formatNumber(resumen?.base ?? 0)} · I {formatNumber(resumen?.igv ?? 0)}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-[11px] font-normal text-ink-500">
                          lista {formatCurrency(resumen?.total_venta ?? 0)}
                        </p>
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="líneas" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Cada fila es una línea del comprobante: el total va con su base «B» e IGV «I» debajo y, a la
        derecha, el total a precio de lista (la columna «total venta» del panel anterior). La opción
        TODOS del producto ya devuelve todo el mes, las boletas «Anulado por NC» no se cuentan como
        venta válida y el tipo Anulado incluye a las notas anuladas, que antes no aparecían.
      </p>
    </div>
  );
}
